<script>
/**
 * Vue 2 wrapper για CookieConsent v3 (by Orest Bida)
 * https://cookieconsent.orestbida.com/
 *
 * npm install vanilla-cookieconsent@3
 *
 * Renderless component: does not render its own markup,
 * CookieConsent creates the modals in the <body> on its own.
 */
import DefaultConfig from '@/theme/default/components/common/cookies/DefaultConfig.js'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import * as CC from 'vanilla-cookieconsent'

// Simple deep merge (arrays are replaced, not joined)
function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof RegExp)
}

function deepMerge(target, source) {
  const out = {...target}
  Object.keys(source || {}).forEach((key) => {
    out[key] = isPlainObject(out[key]) && isPlainObject(source[key])
      ? deepMerge(out[key], source[key])
      : source[key]
  })
  return out
}

export default {
  name: 'CookieConsent',

  props: {
    // Your config is merged onto the default.
    config: {
      type   : Object,
      default: () => ({})
    },
    // true = completely ignores the default config
    replaceDefaults: {
      type   : Boolean,
      default: false
    },
    // Language (e.g. 'el', 'en') — changes live
    lang: {
      type   : String,
      default: null
    }
  },

  data() {
    return {
      ready      : false,
      preferences: null // { acceptType, acceptedCategories, rejectedCategories, ... }
    }
  },

  computed: {
    finalConfig() {
      const base = this.replaceDefaults ? this.config : deepMerge(DefaultConfig, this.config)

      // User callbacks run AND are emitted as Vue events
      const wrap = (name, event) => (payload) => {
        if (typeof base[name] === 'function') base[name](payload)
        this.syncPreferences()
        this.$emit(event, payload)
      }

      return {
        ...base,
        onFirstConsent: wrap('onFirstConsent', 'first-consent'),
        onConsent     : wrap('onConsent', 'consent'),
        onChange      : wrap('onChange', 'change'),
        onModalReady  : wrap('onModalReady', 'modal-ready'),
        onModalShow   : wrap('onModalShow', 'modal-show'),
        onModalHide   : wrap('onModalHide', 'modal-hide')
      }
    }
  },

  watch: {
    lang(newLang) {
      if (newLang && this.ready) CC.setLanguage(newLang)
    }
  },

  async mounted() {
    const config = {...this.finalConfig}
    if (this.lang) {
      config.language = {...config.language, default: this.lang}
    }
    await CC.run(config)
    this.ready = true
    this.syncPreferences()
    this.$emit('ready', CC)
  },

  beforeDestroy() {
    // Removes modals from the DOM, WITHOUT clearing the consent cookie
    CC.reset(false)
  },

  methods: {
    syncPreferences() {
      this.preferences = CC.validConsent() ? CC.getUserPreferences() : null
    },

    // ---- Public API (via ref) ----
    show(createModal = false) {
      CC.show(createModal)
    },
    hide() {
      CC.hide()
    },
    showPreferences() {
      CC.showPreferences()
    },
    hidePreferences() {
      CC.hidePreferences()
    },
    acceptCategory(categories, exclusions) {
      CC.acceptCategory(categories, exclusions)
      this.syncPreferences()
    },
    acceptedCategory(name) {
      return CC.acceptedCategory(name)
    },
    acceptedService(service, category) {
      return CC.acceptedService(service, category)
    },
    validConsent() {
      return CC.validConsent()
    },
    getUserPreferences() {
      return CC.getUserPreferences()
    },
    setLanguage(lang, force) {
      return CC.setLanguage(lang, force)
    },
    eraseCookies(cookies, path, domain) {
      CC.eraseCookies(cookies, path, domain)
    },
    loadScript(src, attrs) {
      return CC.loadScript(src, attrs)
    },
    resetConsent() {
      // Deletes the cookie and shows the banner again
      CC.reset(true)
      this.ready = false
      this.preferences = null
      CC.run({...this.finalConfig}).then(() => {
        this.ready = true
      })
    }
  },

  // Renderless: optional scoped slot for custom UI
  render() {
    const slot = this.$scopedSlots.default
    return slot
      ? slot({
        ready           : this.ready,
        preferences     : this.preferences,
        showPreferences : this.showPreferences,
        acceptedCategory: this.acceptedCategory
      })
      : null
  }
}

// Optional plugin: Vue.use(CookieConsentPlugin) → this.$cc everywhere
export const CookieConsentPlugin = {
  install(Vue) {
    Vue.prototype.$cc = CC
  }
}
</script>
